import React, { useEffect, useState } from "react";
import { DialogWrapMedium } from "../../../controls/components/dialogWrap/DialogWrap";
import { UserFormServiceButtonClose, UserFormServiceButtonCreated } from "../styled/DesStyled";
import { InputOrderAdmin } from "./AddOrderAdmin";
import useOrderDetail from "../hook/useGetOrder";
import useUpdateOrderAdmin from "../hook/updateOrderAdmin";
import { formatStoredMoneyForInput } from "../../Partner/component/moneyInput";
import { orderCalendarDateToPickerDate, toOrderCalendarDateInput } from "../../../../utils/orderCalendarDate";
import { useIsSuperAdmin } from "../../../hooks/useIsSuperAdmin";
import moment from "moment";

// 31/10/2026 (Quân): chỉ super admin được sửa ngày về quá khứ, lùi tối đa 14 ngày, bắt buộc
// lý do. Backend (orderPastDateEditGuard) gác lại đúng luật này; ở đây chỉ để mở lịch và hỏi lý do.
const PAST_DATE_EDIT_MAX_DAYS = 14;
const PAST_DATE_REASON_MIN = 5;

function storageDays(fromYmd: string, toYmd: string) {
	const a = moment(fromYmd, "YYYY-MM-DD", true);
	const b = moment(toYmd, "YYYY-MM-DD", true);
	return a.isValid() && b.isValid() ? b.diff(a, "days") + 1 : null;
}

export default function DialogUpdateOrderAdmin(props: {
	openDialog: boolean;
	setOpenDialog: (value: boolean) => void;
	orderId: number;
	reload: () => void;
}) {
	const { orderDetail } = useOrderDetail({ id: props.orderId.toString(), openDialog: props.openDialog });
	const [partner, setPartner] = useState<number>();
	const [timeIn, setTimeIn] = useState("");
	const [timeOut, setTimeOut] = useState("");
	const [quantity, setQuantity] = useState("");
	const [paymentMethod, setPaymentMethod] = useState("1");
	const [totalMoney, setTotalMoney] = useState("");
	const [status, setStatus] = useState("");
	const [userId, setUserId] = useState(0);
	const [timeInDate, setTimeInDate] = useState("");
	const [timeInTime, setTimeInTime] = useState<Date>();
	const [timeOutDate, setTimeOutDate] = useState("");
	const [timeOutTime, setTimeOutTime] = useState<Date>();
	const { updateOrderAd } = useUpdateOrderAdmin({ orderId: props.orderId, reload: props.reload });
	const [isLoading, setIsLoading] = useState(false);
	const { isSuperAdmin } = useIsSuperAdmin();
	const [pastDateReason, setPastDateReason] = useState("");

	const todayYmd = moment().format("YYYY-MM-DD");
	const origTimeIn = toOrderCalendarDateInput(orderDetail?.timeInString || orderDetail?.timeIn) || "";
	const origTimeOut = toOrderCalendarDateInput(orderDetail?.timeOutString || orderDetail?.timeOut) || "";
	const newTimeIn = toOrderCalendarDateInput(timeIn) || "";
	const newTimeOut = toOrderCalendarDateInput(timeOut) || "";
	const isPastDateEdit =
		(!!newTimeIn && newTimeIn !== origTimeIn && newTimeIn < todayYmd) ||
		(!!newTimeOut && newTimeOut !== origTimeOut && newTimeOut < todayYmd);
	const origDays = storageDays(origTimeIn, origTimeOut);
	const newDays = storageDays(newTimeIn, newTimeOut);
	const pastReasonOk = pastDateReason.trim().length >= PAST_DATE_REASON_MIN;
	const minDate = isSuperAdmin
		? moment().subtract(PAST_DATE_EDIT_MAX_DAYS, "days").startOf("day").toDate()
		: undefined;

	const handleSave = async () => {
		if (new Date(timeIn) > new Date(timeOut)) {
			alert("Thời gian bắt đầu phải nhỏ hơn thời gian kết thúc!");
			return;
		}
		if (isPastDateEdit && !pastReasonOk) {
			alert("Moving dates into the past needs a reason (e.g. the support ticket code).");
			return;
		}
		setIsLoading(true);
		try {
			const allowPartnerId = partner !== orderDetail?.partnerId;
			await updateOrderAd(
				partner,
				timeIn,
				timeOut,
				quantity,
				paymentMethod,
				totalMoney,
				status,
				userId,
				allowPartnerId,
				isPastDateEdit ? pastDateReason.trim() : undefined
			);
			props.setOpenDialog(false);
		} catch (error) {
			console.error("Lỗi khi cập nhật đơn hàng:", error);
		} finally {
			setIsLoading(false);
		}
		props.setOpenDialog(false);
	};
	useEffect(() => {
		if (props.openDialog && orderDetail) {
			setPastDateReason("");
			setPartner(orderDetail?.partnerId || 1);
			setTimeIn(orderDetail?.timeInString || orderDetail?.timeIn || "");
			setTimeOut(orderDetail?.timeOutString || orderDetail?.timeOut || "");
			setQuantity((orderDetail?.quantity || 0).toString());
			setPaymentMethod(orderDetail?.paymentMethod ? orderDetail.paymentMethod.toString() : "1");
			setTotalMoney(formatStoredMoneyForInput(orderDetail?.totalMoney ?? "0"));
			setStatus((orderDetail?.status || "").toString());
			setUserId(orderDetail?.customerUserId || 0);

			const rawTimeIn = orderDetail?.timeInString || orderDetail?.timeIn;
			const parsedTimeIn = toOrderCalendarDateInput(rawTimeIn);
			if (parsedTimeIn) {
				setTimeInDate(parsedTimeIn);
				setTimeInTime(orderCalendarDateToPickerDate(parsedTimeIn) || undefined);
			}

			const rawTimeOut = orderDetail?.timeOutString || orderDetail?.timeOut;
			const parsedTimeOut = toOrderCalendarDateInput(rawTimeOut);
			if (parsedTimeOut) {
				setTimeOutDate(parsedTimeOut);
				setTimeOutTime(orderCalendarDateToPickerDate(parsedTimeOut) || undefined);
			}
		}
	}, [orderDetail, props.openDialog]);

	return (
		<DialogWrapMedium
			login={true}
			open={props.openDialog}
			onClose={() => {
				props.setOpenDialog(false);
			}}
			title="Sửa đơn"
			actions={
				<>
					<UserFormServiceButtonClose onClick={() => props.setOpenDialog(false)}>
						Đóng
					</UserFormServiceButtonClose>
					<UserFormServiceButtonCreated
						onClick={handleSave}
						disabledButton={isLoading}>
						{isLoading ? "Loading..." : "Save"}
					</UserFormServiceButtonCreated>
				</>
			}>
			<>
				<InputOrderAdmin
					updateOrder={true}
					partner={partner}
					partnerLabel={orderDetail?.partner?.displayName || orderDetail?.partner?.name || ""}
					setPartner={setPartner}
					timeIn={timeIn}
					setTimeIn={setTimeIn}
					timeOut={timeOut}
					setTimeOut={setTimeOut}
					quantity={quantity}
					setQuantity={setQuantity}
					paymentMethod={paymentMethod}
					setPaymentMethod={setPaymentMethod}
					totalMoney={totalMoney}
					setTotalMoney={setTotalMoney}
					status={status}
					setStatus={setStatus}
					userId={userId}
					setUserId={setUserId}
					timeInDate={timeInDate}
					timeInTime={timeInTime}
					timeOutDate={timeOutDate}
					timeOutTime={timeOutTime}
					openDialog={props.openDialog}
					minDate={minDate}
				/>
				{isSuperAdmin && (
					<div style={{ fontSize: 12, color: "#6B7280", marginTop: 6 }}>
						Super admin: dates can be moved back up to {PAST_DATE_EDIT_MAX_DAYS} days.
					</div>
				)}
				{isPastDateEdit && (
					<div
						style={{
							marginTop: 12,
							padding: 12,
							borderRadius: 10,
							background: "#FFFBEB",
							border: "1px solid #FCD34D",
							fontSize: 13,
							color: "#78350F",
							lineHeight: 1.5,
						}}>
						<b>Moving dates into the past</b> (super admin only). The customer and station get the
						date-change email; the PIN stays the same. This edit is logged with your reason.
						{origDays != null && newDays != null && origDays !== newDays && (
							<div style={{ marginTop: 6, color: "#B91C1C" }}>
								Storage length changes from {origDays} to {newDays} days, but the total is <b>not</b>{" "}
								recalculated.
							</div>
						)}
						{newTimeOut && newTimeOut < todayYmd && (
							<div style={{ marginTop: 6, color: "#B91C1C" }}>
								The end date is already past: if the station has not verified this booking, it can be
								moved to No-Show automatically. Verify it right after saving.
							</div>
						)}
						<div style={{ marginTop: 8, fontWeight: 600 }}>Reason (required)</div>
						<textarea
							value={pastDateReason}
							onChange={(e) => setPastDateReason(e.target.value)}
							maxLength={500}
							rows={2}
							placeholder="e.g. WL-SUP-01GRCX4B8Q — customer picked the wrong month"
							style={{
								width: "100%",
								boxSizing: "border-box",
								marginTop: 4,
								padding: 8,
								borderRadius: 8,
								border: `1px solid ${pastReasonOk ? "#D1D5DB" : "#F87171"}`,
								fontFamily: "inherit",
								fontSize: 13,
							}}
						/>
					</div>
				)}
			</>
		</DialogWrapMedium>
	);
}
